/**
 * Vista 360° de empresa — resumen ejecutivo en un solo pantallazo.
 *
 * Recibe datos ya cargados en ClienteDetalle (contactos, contratos,
 * visitas, tareas) y fetcha correos recientes on-mount.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Users, FileStack, CalendarDays, ListChecks, Mail,
  CheckSquare, Square, Clock, AlertTriangle, Sparkles,
  ChevronRight, Phone, Building2, Star,
} from 'lucide-react';
import { req } from '../../services/api';
import { toast } from 'sonner';
import { formatFecha } from '../../utils/dateFormat';

// ─── KPI card ────────────────────────────────────────────────────────────────
const KpiCard = ({ icon: Icon, label, value, color, onClick }) => (
  <button
    onClick={onClick}
    className={`flex-1 min-w-[100px] flex flex-col items-center gap-1 p-3 rounded-lg border transition hover:shadow-sm ${color}`}
  >
    <Icon className="w-5 h-5" />
    <span className="text-2xl font-bold leading-none">{value}</span>
    <span className="text-[11px] font-medium text-center leading-tight">{label}</span>
  </button>
);

// ─── Sección con título y enlace "Ver todos" ──────────────────────────────────
const Seccion = ({ titulo, icon: Icon, color, onVerTodos, children }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <h3 className={`text-sm font-semibold flex items-center gap-1.5 ${color}`}>
        <Icon className="w-4 h-4" /> {titulo}
      </h3>
      {onVerTodos && (
        <button onClick={onVerTodos} className="text-[11px] text-brand-600 hover:underline flex items-center gap-0.5">
          Ver todos <ChevronRight className="w-3 h-3" />
        </button>
      )}
    </div>
    {children}
  </div>
);

// ─── Componente principal ─────────────────────────────────────────────────────
export default function Vista360({ entidadId, entidad, contactos, contratos, visitas, tareas, delegaciones = [], onChangeTab }) {
  const navigate = useNavigate();
  const [correos, setCorreos] = useState([]);
  const [loadingCorreos, setLoadingCorreos] = useState(true);
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);

  useEffect(() => {
    setLoadingCorreos(true);
    const ids = [entidadId, ...delegaciones.map((d) => d.id)];
    // Carga correos de la empresa madre + todas sus delegaciones
    Promise.all(ids.map((eid) =>
      req(`/api/correo/sincronizados?entidad_id=${eid}&limit=5`)
        .then((d) => d.items || [])
        .catch(() => [])
    ))
      .then((groups) => {
        const all = groups.flat().sort((a, b) => new Date(b.received_at) - new Date(a.received_at));
        setCorreos(all.slice(0, 5));
      })
      .finally(() => setLoadingCorreos(false));
  }, [entidadId, delegaciones]);

  // Derivados
  const tareasPendientes = tareas.filter((t) => t.estado !== 'hecha' && t.estado !== 'cancelada');
  const proximasTareas = [...tareasPendientes]
    .sort((a, b) => {
      if (!a.vencimiento) return 1;
      if (!b.vencimiento) return -1;
      return new Date(a.vencimiento) - new Date(b.vencimiento);
    })
    .slice(0, 4);

  const ultimasVisitas = [...visitas]
    .sort((a, b) => new Date(b.fecha || 0) - new Date(a.fecha || 0))
    .slice(0, 3);

  const contactosDecisores = contactos.filter((c) => c.es_decisor);
  const contactosMostrar = contactosDecisores.length > 0
    ? contactosDecisores.slice(0, 3)
    : contactos.slice(0, 3);

  return (
    <div className="space-y-5" data-testid="vista-360">

      {/* ── KPIs ─────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-3">
        <KpiCard icon={Users} label="Contactos" value={contactos.length}
          color="border-blue-200 bg-blue-50 text-blue-700"
          onClick={() => onChangeTab('contactos')} />
        <KpiCard icon={ListChecks} label="Tareas pend." value={tareasPendientes.length}
          color={tareasPendientes.length > 0 ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-ink-200 bg-ink-50 text-ink-500'}
          onClick={() => onChangeTab('tareas')} />
        <KpiCard icon={CalendarDays} label="Visitas" value={visitas.length}
          color="border-emerald-200 bg-emerald-50 text-emerald-700"
          onClick={() => onChangeTab('visitas')} />
        {contratos.length > 0 && (
          <KpiCard icon={FileStack} label="Contratos" value={contratos.length}
            color="border-violet-200 bg-violet-50 text-violet-700"
            onClick={() => onChangeTab('contratos')} />
        )}
        <KpiCard icon={Mail} label="Correos" value={loadingCorreos ? '…' : correos.length}
          color="border-brand-200 bg-brand-50 text-brand-700"
          onClick={() => onChangeTab('correos')} />
      </div>

      {/* ── Grid principal ───────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

        {/* Próximas tareas */}
        <Card className="p-4 border-ink-200 bg-white">
          <Seccion titulo="Próximas tareas" icon={ListChecks} color="text-amber-700"
            onVerTodos={() => onChangeTab('tareas')}>
            {proximasTareas.length === 0 ? (
              <p className="text-xs text-ink-400 py-2">Sin tareas pendientes</p>
            ) : (
              <ul className="space-y-2">
                {proximasTareas.map((t) => {
                  const venc = t.vencimiento ? new Date(t.vencimiento) : null;
                  const vencida = venc && venc < hoy;
                  return (
                    <li key={t.id} className="flex items-start gap-2 text-xs">
                      <span className="mt-0.5 shrink-0">
                        {t.estado === 'hecha'
                          ? <CheckSquare className="w-3.5 h-3.5 text-emerald-500" />
                          : <Square className="w-3.5 h-3.5 text-ink-300" />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-ink-800 font-medium truncate">{t.titulo}</p>
                        {t.vencimiento && (
                          <span className={`text-[10px] flex items-center gap-0.5 ${vencida ? 'text-red-600 font-semibold' : 'text-ink-400'}`}>
                            {vencida && <AlertTriangle className="w-3 h-3" />}
                            <Clock className="w-3 h-3" /> {formatFecha(t.vencimiento)}
                          </span>
                        )}
                      </div>
                      <Badge className={`text-[9px] border shrink-0 ${
                        t.urgente && t.importante ? 'bg-red-50 text-red-700 border-red-200'
                        : t.importante ? 'bg-violet-50 text-violet-700 border-violet-200'
                        : t.urgente ? 'bg-amber-50 text-amber-700 border-amber-200'
                        : 'hidden'
                      }`}>
                        {t.urgente && t.importante ? '🟥' : t.importante ? '★' : t.urgente ? '⚡' : ''}
                      </Badge>
                    </li>
                  );
                })}
              </ul>
            )}
          </Seccion>
        </Card>

        {/* Últimas visitas */}
        <Card className="p-4 border-ink-200 bg-white">
          <Seccion titulo="Últimas visitas" icon={CalendarDays} color="text-emerald-700"
            onVerTodos={() => onChangeTab('visitas')}>
            {ultimasVisitas.length === 0 ? (
              <p className="text-xs text-ink-400 py-2">Sin visitas registradas</p>
            ) : (
              <ul className="space-y-2">
                {ultimasVisitas.map((v) => (
                  <li key={v.id} className="text-xs border-l-2 border-emerald-200 pl-2">
                    <p className="text-ink-800 font-medium capitalize">{v.tipo || 'Visita'}</p>
                    <p className="text-ink-500">{formatFecha(v.fecha)}</p>
                    {v.resumen && (
                      <p className="text-ink-400 line-clamp-2 mt-0.5">{v.resumen}</p>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Seccion>
        </Card>

        {/* Correos recientes */}
        <Card className="p-4 border-ink-200 bg-white">
          <Seccion titulo="Correos recientes" icon={Mail} color="text-brand-700"
            onVerTodos={() => onChangeTab('correos')}>
            {loadingCorreos ? (
              <p className="text-xs text-ink-400 py-2">Cargando…</p>
            ) : correos.length === 0 ? (
              <p className="text-xs text-ink-400 py-2">Sin correos sincronizados</p>
            ) : (
              <ul className="space-y-2">
                {correos.map((m) => (
                  <li key={m.message_id} className="text-xs">
                    <p className="text-ink-800 font-medium truncate">{m.subject || '(sin asunto)'}</p>
                    <p className="text-[10px] text-ink-400 flex items-center gap-1">
                      <Mail className="w-3 h-3 shrink-0" />
                      <span className="truncate">{m.from_name || m.from_address}</span>
                      <span className="shrink-0">·</span>
                      <span className="shrink-0">{m.received_at ? new Date(m.received_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }) : ''}</span>
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Seccion>
        </Card>
      </div>

      {/* ── Contactos clave ───────────────────────────────────────────── */}
      {contactosMostrar.length > 0 && (
        <Card className="p-4 border-ink-200 bg-white">
          <Seccion titulo={contactosDecisores.length > 0 ? 'Decisores' : 'Contactos'}
            icon={Users} color="text-blue-700"
            onVerTodos={() => onChangeTab('contactos')}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-1">
              {contactosMostrar.map((c) => (
                <div key={c.id} className="flex items-start gap-2.5 p-2.5 rounded-lg border border-ink-100 bg-ink-50/40 hover:bg-white hover:shadow-sm transition">
                  <div className="h-8 w-8 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center font-semibold text-sm shrink-0">
                    {c.nombre?.[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="text-xs font-semibold text-ink-900 truncate">
                        {c.nombre} {c.apellidos || ''}
                      </p>
                      {c.es_decisor && <Star className="w-3 h-3 text-amber-500 shrink-0" title="Decisor" />}
                    </div>
                    {c.cargo && <p className="text-[10px] text-ink-500 truncate">{c.cargo}</p>}
                    {c.email_trabajo && (
                      <a href={`mailto:${c.email_trabajo}`} className="text-[10px] text-brand-600 hover:underline truncate flex items-center gap-0.5">
                        <Mail className="w-3 h-3 shrink-0" />{c.email_trabajo}
                      </a>
                    )}
                    {(c.movil_trabajo || c.telefono_trabajo) && (
                      <p className="text-[10px] text-ink-500 flex items-center gap-0.5">
                        <Phone className="w-3 h-3 shrink-0" />{c.movil_trabajo || c.telefono_trabajo}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Seccion>
        </Card>
      )}

      {/* ── Info clave de la empresa ─────────────────────────────────── */}
      {entidad && (
        <Card className="p-4 border-ink-200 bg-white">
          <Seccion titulo="Datos clave" icon={Building2} color="text-ink-700"
            onVerTodos={() => onChangeTab('datos')}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-1 text-xs">
              {entidad.cif && (
                <div>
                  <p className="text-ink-400 uppercase text-[10px] font-semibold mb-0.5">CIF / NIF</p>
                  <p className="text-ink-800 font-medium">{entidad.cif}</p>
                </div>
              )}
              {entidad.provincia && (
                <div>
                  <p className="text-ink-400 uppercase text-[10px] font-semibold mb-0.5">Provincia</p>
                  <p className="text-ink-800">{entidad.provincia}</p>
                </div>
              )}
              {entidad.telefono && (
                <div>
                  <p className="text-ink-400 uppercase text-[10px] font-semibold mb-0.5">Teléfono</p>
                  <p className="text-ink-800">{entidad.telefono}</p>
                </div>
              )}
              {entidad.web && (
                <div>
                  <p className="text-ink-400 uppercase text-[10px] font-semibold mb-0.5">Web</p>
                  <a href={entidad.web.startsWith('http') ? entidad.web : `https://${entidad.web}`}
                    target="_blank" rel="noreferrer"
                    className="text-brand-600 hover:underline truncate block">{entidad.web}</a>
                </div>
              )}
              {entidad.sector && (
                <div>
                  <p className="text-ink-400 uppercase text-[10px] font-semibold mb-0.5">Sector</p>
                  <p className="text-ink-800">{entidad.sector}</p>
                </div>
              )}
              {entidad.facturacion && (
                <div>
                  <p className="text-ink-400 uppercase text-[10px] font-semibold mb-0.5">Facturación</p>
                  <p className="text-ink-800">{entidad.facturacion}</p>
                </div>
              )}
              {entidad.empleados_aprox && (
                <div>
                  <p className="text-ink-400 uppercase text-[10px] font-semibold mb-0.5">Empleados</p>
                  <p className="text-ink-800">~{entidad.empleados_aprox.toLocaleString('es-ES')}</p>
                </div>
              )}
              {entidad.comercial_asignado_nombre && (
                <div>
                  <p className="text-ink-400 uppercase text-[10px] font-semibold mb-0.5">Comercial</p>
                  <p className="text-ink-800">{entidad.comercial_asignado_nombre}</p>
                </div>
              )}
            </div>
          </Seccion>
        </Card>
      )}

    </div>
  );
}
